import { EscalationPlatform, EscalationQueuingStrategyType, EscalationsGroupInterface, RoleInterfaceMap, Slug } from "@swivl-mono/types";
import { Dropdown, Input, RadioButtons, SelectableOption, Spinner, Switch } from "@swivl-mono/ui";
import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { QuestsDropdown } from "../../../../Components/Dropdowns/QuestsDropdown/QuestsDropdown";
import { InheritanceTag } from "../../../../Components/InheritanceTag/InheritanceTag";
import { TagInput } from "../../../../Components/TagInput/TagInput";
import EscalationsModel from "../../../../Models/Escalations/Escalations.Model";
import "./EscalationGroupView.scss";
import { EditRolesView } from "./components/AddRolesView";
import HoursPicker from "./components/HoursPicker/HoursPicker";
import { MembersSelector } from "./components/MembersSelector/MembersSelector";

interface Props {
  group: EscalationsGroupInterface;
  agents: any;
  roles?: RoleInterfaceMap;
  slackConversations: any;
}

export default function EscalationGroupView({ group, agents, roles, slackConversations }: Props) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const groupState = useRef<EscalationsGroupInterface>({ ...group });
  const [currentGroup, setCurrentGroup] = useState<EscalationsGroupInterface>({ ...group });
  const autosaveRef = useRef(0);
  const autosaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // keep groupstate ref in sync
  useEffect(() => {
    groupState.current = currentGroup;
  }, [currentGroup]);

  // autosave effect
  useEffect(() => {
    autosaveIntervalRef.current = setInterval(() => {
      if (!autosaveRef.current) return;
      if (Date.now() - autosaveRef.current > 3000) {
        autosaveRef.current = 0;
        EscalationsModel.updateGroup(groupState.current, true);
      }
    }, 2000);
    return () => {
      if (autosaveRef.current) {
        EscalationsModel.updateGroup(groupState.current, true);
      }
      if (autosaveIntervalRef.current) {
        clearInterval(autosaveIntervalRef.current);
      }
    };
  }, []);

  const toggleCollapsed = useCallback(() => setIsCollapsed((c) => !c), []);

  const removeGroup = useCallback(() => {
    if (window.confirm("Are you sure you want to remove this group? This action cannot be undone")) {
      EscalationsModel.deleteGroup(groupState.current);
    }
  }, []);

  const onPlatformChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value as keyof typeof EscalationPlatform;
    let platform: EscalationPlatform = EscalationPlatform[value.toUpperCase() as keyof typeof EscalationPlatform] || EscalationPlatform.SWIVL;
    const updatedGroup = { ...groupState.current, platform };
    setCurrentGroup(updatedGroup);
    autosaveRef.current = Date.now();
    EscalationsModel.updateGroup(updatedGroup, false);
  }, []);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    _onChange(e, false);
  }, []);

  const onChangeAndSave = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    _onChange(e, true);
  }, []);

  const _onChange = (e: React.ChangeEvent<any>, save: boolean) => {
    const updatedGroup = { ...groupState.current };
    const { name, value } = e.target;

    if (name === "name") {
      updatedGroup.name = value;
    } else if (name === "slackChannelId") {
      updatedGroup.slackChannelId = value;
    } else {
      _.set(updatedGroup, name, value);
    }

    setCurrentGroup(updatedGroup);
    autosaveRef.current = Date.now();
    EscalationsModel.updateGroup(updatedGroup, save);
  };

  const questSelected = useCallback(
    (questSlug: Slug, name: string) => {
      onChangeAndSave({ target: { value: questSlug, name } } as any);
    },
    [onChangeAndSave],
  );

  const queuingStrategySelected = useCallback(
    (option: SelectableOption, name: string) => {
      onChangeAndSave({ target: { value: option.value, name } } as any);
    },
    [onChangeAndSave],
  );

  const membersQueuingOrderChanged = useCallback(
    (memberIds: number[]) => {
      onChangeAndSave({ target: { value: memberIds, name: "membersQueuingOrder" } } as any);
    },
    [onChangeAndSave],
  );

  const renderHeader = () => {
    const closeIcon = isCollapsed ? <FaChevronDown /> : <FaChevronUp />;
    const nameField = isCollapsed ? (
      <h3 onClick={toggleCollapsed}>{group.name}</h3>
    ) : (
      <input value={group.name} onChange={onChange} placeholder="Group name..." name="name" />
    );

    return (
      <header>
        {group.inheritance && <InheritanceTag inheritance={group.inheritance} />}
        {nameField}
        <div className="removeButton" onClick={removeGroup}>
          REMOVE
        </div>
        <div className="arrowButton" onClick={toggleCollapsed}>
          {closeIcon}
        </div>
      </header>
    );
  };

  const renderSlackSettings = () => null;

  const queuingStrategies: SelectableOption[] = [
    { label: "Longest idle first", value: EscalationQueuingStrategyType.LONGEST_IDLE_FIRST },
    { label: "Ring specific agent", value: EscalationQueuingStrategyType.RING_SPECIFIC_MEMBER },
  ];

  const renderQueuing = () => {
    const slackSettings = group.platform === "slack" ? renderSlackSettings() : null;
    return (
      <>
        <label>Queuing Strategy</label>
        <Dropdown options={queuingStrategies} value={group.queuingStrategy} name="queuingStrategy" optionSelected={queuingStrategySelected as any} />

        {group.queuingStrategy === EscalationQueuingStrategyType.RING_SPECIFIC_MEMBER && (
          <MembersSelector selectedMemberIds={group.membersQueuingOrder} onMembersChange={membersQueuingOrderChanged} />
        )}

        <label>Multiple Escalation Response</label>
        <Input onChange={onChange} name="multipleEscalationsResponse" value={group.multipleEscalationsResponse} />

        <label>No Agent Available Quest</label>
        <QuestsDropdown value={group.noAvailableAgentsQuestSlug} questSelected={questSelected} name="noAvailableAgentsQuestSlug" />

        <label>Time before skipping agent (seconds)</label>
        <Input onChange={onChange} name="secondsToWaitForAgent" value={String(group.secondsToWaitForAgent)} />

        <label>Tries before going to “no agent available” quest</label>
        <Input onChange={onChange} name="numberOfAgentsToTry" value={String(group.numberOfAgentsToTry)} />

        {slackSettings}
      </>
    );
  };

  if (isCollapsed) {
    return <div className="EscalationGroup">{renderHeader()}</div>;
  }

  return (
    <div className="EscalationGroup">
      {renderHeader()}

      <label>Initial Message to user</label>
      <TagInput value={group.initialMessage} onChange={onChange} name="initialMessage" />

      <label>Hours</label>
      <HoursPicker hours={group.hours} onChange={onChangeAndSave} timezoneName="timezone" />

      <label>Outside Hours Quest</label>
      <QuestsDropdown value={group.outsideHoursQuestSlug} questSelected={questSelected} name="outsideHoursQuestSlug" />

      <label>Ignore Availability</label>
      <Switch on={!!group.ignoreAvailability} name="ignoreAvailability" onChange={onChange} />

      <label>Channel to send escalations to</label>
      <RadioButtons
        value={group.platform}
        options={[
          { value: "swivl", label: "Swivl" },
          { value: "slack", label: "Slack" },
        ]}
        name="platform"
        onChange={onPlatformChange}
      />

      {roles ? <EditRolesView roles={roles} group={group} /> : <Spinner />}

      {group.queue ? renderQueuing() : null}
    </div>
  );
}
